import { useEffect, useState } from "react"

type ScrollOverflow = {
  hasTopOverflow: boolean
  hasBottomOverflow: boolean
  hasLeftOverflow: boolean
  hasRightOverflow: boolean
}

const NO_OVERFLOW: ScrollOverflow = {
  hasTopOverflow: false,
  hasBottomOverflow: false,
  hasLeftOverflow: false,
  hasRightOverflow: false
}

/**
 * Tracks whether a scroll container has content clipped beyond any of its four
 * edges. Attach the same `ref` to the scrollable element.
 *
 * Each flag is true while the element is scrollable on that axis and not
 * scrolled all the way to that edge, so `hasRightOverflow` means "there's more
 * content off to the right". All four are false when the element can't scroll.
 *
 * Both axes are measured together because it costs nothing: the reads happen in
 * one batch inside a single animation frame, and the scroll listener and
 * observers below are shared. Callers destructure only the axis they care
 * about.
 *
 * The state re-evaluates on scroll, when the element resizes (which affects
 * scrollability), and when its contents change — so a caller can, for example,
 * reveal an edge shadow only while there's hidden content in that direction.
 *
 * @example
 * const ref = useRef<HTMLUListElement>(null)
 * const { hasBottomOverflow } = useTrackScrollOverflow(ref)
 * return <ul ref={ref} className={cx(hasBottomOverflow && "shadow-...")}>...</ul>
 */
export function useTrackScrollOverflow(
  ref: React.RefObject<HTMLElement>
): ScrollOverflow {
  const [overflow, setOverflow] = useState(NO_OVERFLOW)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    let frame = 0
    const measure = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        const {
          scrollTop,
          scrollHeight,
          clientHeight,
          scrollLeft,
          scrollWidth,
          clientWidth
        } = el
        const isScrollableY = scrollHeight > clientHeight
        const isScrollableX = scrollWidth > clientWidth
        // Round up so sub-pixel scroll extents still resolve to "at the end".
        const isAtBottom = Math.ceil(scrollTop + clientHeight) >= scrollHeight
        const isAtRight = Math.ceil(scrollLeft + clientWidth) >= scrollWidth
        const next: ScrollOverflow = {
          hasTopOverflow: isScrollableY && scrollTop > 0,
          hasBottomOverflow: isScrollableY && !isAtBottom,
          hasLeftOverflow: isScrollableX && scrollLeft > 0,
          hasRightOverflow: isScrollableX && !isAtRight
        }
        setOverflow(prev =>
          prev.hasTopOverflow === next.hasTopOverflow &&
          prev.hasBottomOverflow === next.hasBottomOverflow &&
          prev.hasLeftOverflow === next.hasLeftOverflow &&
          prev.hasRightOverflow === next.hasRightOverflow
            ? prev
            : next
        )
      })
    }
    measure()

    el.addEventListener("scroll", measure, { passive: true })
    // ResizeObserver catches changes in scrollability; MutationObserver catches
    // items being added or removed.
    const resizeObserver = new ResizeObserver(measure)
    resizeObserver.observe(el)
    const mutationObserver = new MutationObserver(measure)
    mutationObserver.observe(el, { childList: true, subtree: true })

    return () => {
      cancelAnimationFrame(frame)
      el.removeEventListener("scroll", measure)
      resizeObserver.disconnect()
      mutationObserver.disconnect()
    }
  }, [ref])

  return overflow
}
